import { useId } from 'react';
import { motion } from 'framer-motion';
import { Link2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import {
  LINK_TYPE_LEGEND,
  linkPillClass,
  predecessorDisplayLabel,
  resolvePredecessorTaskIndex,
  resolveSuccessorTaskIndex,
  successorDisplayLabel,
  type EpcTaskRef,
  type ParsedPredecessorLink,
  type ParsedSuccessorLink,
} from './utils/epcScheduleDependencies';

const LINK_ACCENT: Record<string, string> = {
  FS: 'text-slate-600',
  SS: 'text-sky-600',
  FF: 'text-violet-600',
  SF: 'text-amber-700',
};

const LINK_CARD_BG: Record<string, string> = {
  FS: 'from-slate-500/12 via-slate-400/5 to-transparent',
  SS: 'from-sky-500/15 via-sky-400/5 to-transparent',
  FF: 'from-violet-500/15 via-violet-400/5 to-transparent',
  SF: 'from-amber-500/18 via-amber-400/6 to-transparent',
};

/** Subtle left accent for link-type showcase cards. */
const LINK_CARD_ACCENT: Record<string, string> = {
  FS: 'border-l-[3px] border-l-slate-400/75',
  SS: 'border-l-[3px] border-l-sky-500/80',
  FF: 'border-l-[3px] border-l-violet-500/80',
  SF: 'border-l-[3px] border-l-amber-500/85',
};

const SHOWCASE_ACCENT_HEX: Record<string, string> = {
  FS: '#475569',
  SS: '#0284c7',
  FF: '#7c3aed',
  SF: '#b45309',
};

const SHOWCASE_EASE = [0.45, 0, 0.55, 1] as const;
const SHOWCASE_CYCLE = 6;

function barProgressTransition(
  code: string,
  role: 'pred' | 'task',
  delay: number,
  wPred: number,
  wTask: number
): { width: number[]; transition: Record<string, unknown> } {
  // FS & SF: first task completes toward finish, then second task starts (sequential fill).
  if (code === 'FS' || code === 'SF') {
    if (role === 'pred') {
      return {
        width: [0, wPred, wPred, 0, 0],
        transition: {
          duration: SHOWCASE_CYCLE,
          times: [0, 0.14, 0.26, 0.28, 1],
          repeat: Infinity,
          ease: SHOWCASE_EASE,
          delay,
        },
      };
    }
    return {
      width: [0, 0, 0, wTask, wTask, 0],
        transition: {
          duration: SHOWCASE_CYCLE,
          times: [0, 0.26, 0.28, 0.28, 0.46, 0.58],
          repeat: Infinity,
          ease: SHOWCASE_EASE,
          delay,
        },
    };
  }
  // SS & FF: both tasks advance together (parallel fill).
  const w = role === 'pred' ? wPred : wTask;
  return {
    width: [0, w, w, 0],
    transition: {
      duration: SHOWCASE_CYCLE,
      times: [0, 0.12, 0.28, 0.4],
      repeat: Infinity,
      ease: SHOWCASE_EASE,
      delay,
    },
  };
}

/** MS Project–style diagram: two bars, S/F anchors, link-type–aware work fill (no extra labels). */
function ShowcaseDependencyGraphic({
  code,
  className,
  animationIndex = 0,
}: {
  code: string;
  className?: string;
  animationIndex?: number;
}) {
  const uid = useId().replace(/:/g, '');
  const hex = SHOWCASE_ACCENT_HEX[code] ?? '#64748b';
  const delay = 0.06 + animationIndex * 0.05;
  const flowDelay = delay + 0.75;

  /** Pred finish meets task start (x = 39) for a clean FS hinge. */
  const barPred = { x: 9, y: 8, w: 30, h: 5.5, rx: 2.75 };
  const barThis = { x: 39, y: 19.5, w: 30, h: 5.5, rx: 2.75 };
  const midY = (b: typeof barPred) => b.y + b.h / 2;
  const sPred = { x: barPred.x, y: midY(barPred) };
  const fPred = { x: barPred.x + barPred.w, y: midY(barPred) };
  const sThis = { x: barThis.x, y: midY(barThis) };
  const fThis = { x: barThis.x + barThis.w, y: midY(barThis) };

  let linkD = '';
  if (code === 'FS') linkD = `M ${fPred.x} ${fPred.y} L ${sThis.x} ${sThis.y}`;
  else if (code === 'SS') linkD = `M ${sPred.x} ${sPred.y} L ${sThis.x} ${sThis.y}`;
  else if (code === 'FF') linkD = `M ${fPred.x} ${fPred.y} L ${fThis.x} ${fThis.y}`;
  else if (code === 'SF') linkD = `M ${sPred.x} ${sPred.y} L ${fThis.x} ${fThis.y}`;
  else linkD = `M ${fPred.x} ${fPred.y} L ${sThis.x} ${sThis.y}`;

  const sfFs = 3.15;
  const predProg = barProgressTransition(code, 'pred', delay, barPred.w, barThis.w);
  const taskProg = barProgressTransition(code, 'task', delay, barPred.w, barThis.w);

  return (
    <motion.svg
      viewBox="0 0 78 31"
      className={twMerge('h-[2.65rem] w-full max-w-[6.25rem] shrink-0', className)}
      aria-hidden
      initial={{ opacity: 0.92 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '0px 0px -24px 0px' }}
    >
      <defs>
        <linearGradient id={`sg-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={hex} stopOpacity="0.35" />
          <stop offset="50%" stopColor={hex} stopOpacity="0.95" />
          <stop offset="100%" stopColor={hex} stopOpacity="0.5" />
        </linearGradient>
        <marker id={`mk-${uid}`} markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 Z" fill={hex} />
        </marker>
        <filter id={`glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="0.75" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Dashed flow (behind bars) */}
      <motion.path
        d={linkD}
        fill="none"
        stroke={hex}
        strokeOpacity={0.2}
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="2 4.5"
        vectorEffect="non-scaling-stroke"
        initial={{ opacity: 0, pathLength: 0 }}
        whileInView={{ opacity: 1, pathLength: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        animate={{ strokeDashoffset: [0, -18] }}
        transition={{
          pathLength: { duration: 0.85, ease: SHOWCASE_EASE, delay: delay + 0.06 },
          opacity: { duration: 0.4, ease: SHOWCASE_EASE, delay: delay + 0.06 },
          strokeDashoffset: { duration: 3.2, repeat: Infinity, ease: 'linear', delay: flowDelay },
        }}
      />

      <motion.rect
        x={barPred.x}
        y={barPred.y}
        width={barPred.w}
        height={barPred.h}
        rx={barPred.rx}
        fill="#f1f5f9"
        stroke={hex}
        strokeOpacity={0.35}
        strokeWidth="0.55"
        initial={{ opacity: 0, y: barPred.y - 3 }}
        whileInView={{ opacity: 1, y: barPred.y }}
        viewport={{ once: true }}
        transition={{ type: 'spring', stiffness: 320, damping: 30, mass: 0.65, delay }}
      />
      <motion.rect
        x={barThis.x}
        y={barThis.y}
        width={barThis.w}
        height={barThis.h}
        rx={barThis.rx}
        fill="#ffffff"
        stroke={hex}
        strokeOpacity={0.4}
        strokeWidth="0.55"
        initial={{ opacity: 0, y: barThis.y + 3 }}
        whileInView={{ opacity: 1, y: barThis.y }}
        viewport={{ once: true }}
        transition={{ type: 'spring', stiffness: 320, damping: 30, mass: 0.65, delay: delay + 0.06 }}
      />

      {/* Work done: FS/SF = first fills to finish, then second starts; SS/FF = together */}
      <motion.rect
        x={barPred.x + 0.4}
        y={barPred.y + 0.45}
        height={barPred.h - 0.9}
        rx={barPred.rx - 0.45}
        fill={hex}
        fillOpacity={0.42}
        initial={{ width: 0 }}
        animate={{ width: predProg.width }}
        transition={predProg.transition}
      />
      <motion.rect
        x={barThis.x + 0.4}
        y={barThis.y + 0.45}
        height={barThis.h - 0.9}
        rx={barThis.rx - 0.45}
        fill={hex}
        fillOpacity={0.38}
        initial={{ width: 0 }}
        animate={{ width: taskProg.width }}
        transition={taskProg.transition}
      />

      <motion.path
        d={linkD}
        fill="none"
        stroke={`url(#sg-${uid})`}
        strokeWidth="1.55"
        strokeLinecap="round"
        strokeLinejoin="round"
        markerEnd={`url(#mk-${uid})`}
        filter={`url(#glow-${uid})`}
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0, opacity: 0.2 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        animate={{ opacity: [0.9, 1, 0.9] }}
        transition={{
          pathLength: { duration: 1.05, ease: SHOWCASE_EASE, delay: delay + 0.1 },
          opacity: { duration: 3.5, repeat: Infinity, ease: SHOWCASE_EASE, delay: flowDelay + 0.35 },
        }}
      />

      <text x={sPred.x} y={sPred.y + 1.15} fill="#0f172a" fontSize={sfFs} fontWeight="800" textAnchor="middle" opacity="0.95">
        S
      </text>
      <text x={fPred.x} y={fPred.y + 1.15} fill="#0f172a" fontSize={sfFs} fontWeight="800" textAnchor="middle" opacity="0.95">
        F
      </text>
      <text x={sThis.x} y={sThis.y + 1.15} fill="#0f172a" fontSize={sfFs} fontWeight="800" textAnchor="middle" opacity="0.95">
        S
      </text>
      <text x={fThis.x} y={fThis.y + 1.15} fill="#0f172a" fontSize={sfFs} fontWeight="800" textAnchor="middle" opacity="0.95">
        F
      </text>
    </motion.svg>
  );
}

/** Tiny timeline graphic: predecessor (P) → linkage → successor/current (C). */
export function LinkTypeDiagram({ code, className }: { code: string; className?: string }) {
  const uid = useId().replace(/:/g, '');
  const c = LINK_ACCENT[code] ?? 'text-slate-500';
  return (
    <svg
      viewBox="0 0 52 18"
      className={twMerge('h-10 w-[4.5rem] shrink-0 drop-shadow-sm', c, className)}
      aria-hidden
    >
      <defs>
        <marker id={`a-${uid}`} markerWidth="4" markerHeight="4" refX="3.5" refY="2" orient="auto">
          <path d="M0,0 L4,2 L0,4 Z" fill="currentColor" opacity="0.85" />
        </marker>
      </defs>
      {code === 'FS' && (
        <g>
          <text x="0" y="5" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            P
          </text>
          <rect x="6" y="1.5" width="14" height="4" rx="0.8" fill="currentColor" opacity="0.25" />
          <circle cx="20" cy="3.5" r="1.6" fill="currentColor" />
          <line x1="22" y1="3.5" x2="30" y2="3.5" stroke="currentColor" strokeWidth="1" markerEnd={`url(#a-${uid})`} />
          <text x="0" y="15" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            C
          </text>
          <rect x="32" y="11.5" width="18" height="4" rx="0.8" fill="currentColor" opacity="0.18" />
          <circle cx="32" cy="13.5" r="1.6" fill="currentColor" opacity="0.95" />
        </g>
      )}
      {code === 'SS' && (
        <g>
          <text x="0" y="5" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            P
          </text>
          <rect x="6" y="1.5" width="14" height="4" rx="0.8" fill="currentColor" opacity="0.25" />
          <circle cx="6" cy="3.5" r="1.6" fill="currentColor" />
          <line x1="8" y1="3.5" x2="16" y2="10" stroke="currentColor" strokeWidth="0.9" markerEnd={`url(#a-${uid})`} />
          <text x="18" y="15" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            C
          </text>
          <rect x="24" y="11.5" width="14" height="4" rx="0.8" fill="currentColor" opacity="0.18" />
          <circle cx="24" cy="13.5" r="1.6" fill="currentColor" opacity="0.95" />
        </g>
      )}
      {code === 'FF' && (
        <g>
          <text x="0" y="5" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            P
          </text>
          <rect x="6" y="1.5" width="14" height="4" rx="0.8" fill="currentColor" opacity="0.25" />
          <circle cx="20" cy="3.5" r="1.6" fill="currentColor" />
          <line x1="20" y1="5" x2="28" y2="12" stroke="currentColor" strokeWidth="0.9" markerEnd={`url(#a-${uid})`} />
          <text x="18" y="15" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            C
          </text>
          <rect x="30" y="11.5" width="14" height="4" rx="0.8" fill="currentColor" opacity="0.18" />
          <circle cx="44" cy="13.5" r="1.6" fill="currentColor" opacity="0.95" />
        </g>
      )}
      {code === 'SF' && (
        <g>
          <text x="0" y="5" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            P
          </text>
          <rect x="6" y="1.5" width="14" height="4" rx="0.8" fill="currentColor" opacity="0.25" />
          <circle cx="6" cy="3.5" r="1.6" fill="currentColor" />
          <line x1="8" y1="3.5" x2="36" y2="13.5" stroke="currentColor" strokeWidth="0.9" markerEnd={`url(#a-${uid})`} />
          <text x="28" y="15" fill="currentColor" fontSize="4" fontWeight="700" opacity="0.4">
            C
          </text>
          <rect x="32" y="11.5" width="18" height="4" rx="0.8" fill="currentColor" opacity="0.18" />
          <circle cx="50" cy="13.5" r="1.6" fill="currentColor" opacity="0.95" />
        </g>
      )}
      {code !== 'FS' && code !== 'SS' && code !== 'FF' && code !== 'SF' && (
        <g>
          <rect x="4" y="6" width="18" height="5" rx="1" fill="currentColor" opacity="0.2" />
          <line x1="24" y1="8.5" x2="36" y2="8.5" stroke="currentColor" strokeWidth="1" markerEnd={`url(#a-${uid})`} />
          <rect x="38" y="6" width="10" height="5" rx="1" fill="currentColor" opacity="0.15" />
        </g>
      )}
    </svg>
  );
}

function legendHint(code: string): string {
  return LINK_TYPE_LEGEND.find((x) => x.code === code)?.hint ?? '';
}

const showcaseContainer = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const showcaseCard = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 280, damping: 34, mass: 0.72 },
  },
};

/** Tiny pill that summarises how often a link type appears in real schedules. */
function FrequencyPill({ frequency }: { frequency: string }) {
  const styles: Record<string, string> = {
    'most-common': 'bg-emerald-50 text-emerald-700 ring-emerald-200/80',
    common: 'bg-sky-50 text-sky-700 ring-sky-200/80',
    occasional: 'bg-violet-50 text-violet-700 ring-violet-200/80',
    rare: 'bg-amber-50 text-amber-800 ring-amber-200/80',
  };
  const labels: Record<string, string> = {
    'most-common': 'Most common',
    common: 'Common',
    occasional: 'Occasional',
    rare: 'Rare',
  };
  return (
    <span
      className={twMerge(
        'inline-flex items-center rounded-full px-1.5 py-px text-[8.5px] font-semibold uppercase tracking-wide ring-1',
        styles[frequency] ?? 'bg-slate-50 text-slate-600 ring-slate-200/80'
      )}
    >
      {labels[frequency] ?? frequency}
    </span>
  );
}

export function LinkTypeShowcase() {
  return (
    <motion.section
      variants={showcaseContainer}
      initial="hidden"
      animate="show"
      className="relative overflow-hidden rounded-2xl border border-slate-200/85 bg-white shadow-sm ring-1 ring-slate-100/70"
      aria-label="Schedule dependency link types"
    >
      {/* Compact header */}
      <div className="flex items-center gap-2 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 via-white to-slate-50/40 px-3 py-2">
        <span
          className="h-4 w-1 shrink-0 rounded-full bg-gradient-to-b from-teal-500 to-teal-600 shadow-sm shadow-teal-500/30"
          aria-hidden
        />
        <Link2 className="h-3.5 w-3.5 shrink-0 text-teal-600" aria-hidden />
        <h2 className="text-[12px] font-semibold tracking-tight text-slate-900">Link types</h2>
        <span className="hidden text-[10.5px] text-slate-500 sm:inline">
          · How tasks depend on each other (4 standard types)
        </span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-slate-100/70 px-1.5 py-0.5 text-[9.5px] font-medium text-slate-600 ring-1 ring-slate-200/60">
          <span className="font-mono font-bold text-slate-700">P</span>
          <span aria-hidden>→</span>
          <span className="font-mono font-bold text-slate-700">C</span>
          <span className="ml-0.5 text-slate-500">predecessor → current</span>
        </span>
      </div>

      {/* Compact 4-up legend strip */}
      <motion.ul
        className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-y-0 sm:divide-x lg:grid-cols-4"
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.06, delayChildren: 0.08 } },
        }}
      >
        {LINK_TYPE_LEGEND.map((item, i) => (
          <motion.li
            key={item.code}
            variants={showcaseCard}
            title={`${item.label} — ${item.hint}\nExample: ${item.example}`}
            aria-label={`${item.label}. ${item.hint} Example: ${item.example}.`}
            className={twMerge(
              'group relative flex items-start gap-2 px-3 py-2 transition-colors hover:bg-slate-50/70',
              LINK_CARD_ACCENT[item.code] ?? 'border-l-slate-300/60'
            )}
          >
            {/* Mini animated diagram */}
            <div className="flex shrink-0 flex-col items-center gap-1 pt-0.5">
              <ShowcaseDependencyGraphic
                code={item.code}
                animationIndex={i}
                className="h-8 w-[3.6rem]"
              />
              <span
                className={twMerge(
                  'rounded px-1.5 py-px font-mono text-[9.5px] font-black tracking-wider',
                  linkPillClass(item.code)
                )}
              >
                {item.code}
              </span>
            </div>

            {/* Meaning */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={twMerge(
                    'text-[12px] font-semibold leading-tight',
                    LINK_ACCENT[item.code] ?? 'text-slate-700'
                  )}
                >
                  {item.short}
                </span>
                <span className="text-[10px] font-medium text-slate-400">
                  {item.label}
                </span>
                <FrequencyPill frequency={item.frequency} />
              </div>
              <p className="mt-0.5 line-clamp-2 text-[10.5px] leading-snug text-slate-600">
                {item.hint}
              </p>
              <p className="mt-1 inline-flex max-w-full items-center gap-1 truncate rounded bg-slate-50 px-1.5 py-0.5 text-[9.5px] font-medium text-slate-500 ring-1 ring-slate-200/60">
                <span className="text-[8px] font-bold uppercase tracking-wide text-slate-400">
                  e.g.
                </span>
                <span className="truncate">{item.example}</span>
              </p>
            </div>
          </motion.li>
        ))}
      </motion.ul>

      {/* Footnote: lag/lead support */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/60 px-3 py-1.5 text-[10px] text-slate-500">
        <span>
          <span className="font-semibold text-slate-700">Lag</span> adds a wait
          (e.g.{' '}
          <span className="rounded bg-white px-1 py-px font-mono text-[9.5px] text-slate-700 ring-1 ring-slate-200/70">
            FS+2d
          </span>
          ).{' '}
          <span className="font-semibold text-slate-700">Lead</span> overlaps
          (e.g.{' '}
          <span className="rounded bg-white px-1 py-px font-mono text-[9.5px] text-slate-700 ring-1 ring-slate-200/70">
            FS-1d
          </span>
          ).
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="font-mono font-bold text-slate-700">S</span> = Start
          <span className="px-1 text-slate-300">·</span>
          <span className="font-mono font-bold text-slate-700">F</span> = Finish
        </span>
      </div>
    </motion.section>
  );
}

export function LagBadge({ lag }: { lag: string }) {
  return (
    <span className="shrink-0 rounded bg-white/70 px-1 py-0.5 font-mono text-[8px] font-semibold tabular-nums text-slate-500 ring-1 ring-slate-200/70">
      {lag}
    </span>
  );
}

export function CompactPredecessorLinks({
  links,
  tasks,
}: {
  links: ParsedPredecessorLink[];
  tasks: EpcTaskRef[];
}) {
  if (!links.length) {
    return (
      <span className="inline-flex items-center rounded-lg bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-400">
        —
      </span>
    );
  }
  const show = links.slice(0, 2);
  const rest = links.length - show.length;
  return (
    <div className="flex flex-col gap-1.5">
      {show.map((lk, idx) => (
        <div
          key={idx}
          className={twMerge(
            'flex min-w-0 items-start gap-2 rounded-xl border border-white/60 bg-gradient-to-r px-2 py-1.5 shadow-sm ring-1 ring-slate-200/50',
            LINK_CARD_BG[lk.linkTypeCode] ?? 'from-slate-400/8 to-white/80'
          )}
        >
          <LinkTypeDiagram
            code={lk.linkTypeCode}
            className="h-11 w-[4.75rem] shrink-0 opacity-[0.98] drop-shadow"
            aria-hidden
          />
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-center gap-1">
              <span
                className={twMerge(
                  'rounded px-1 py-0.5 font-mono text-[8px] font-black opacity-90',
                  linkPillClass(lk.linkTypeCode)
                )}
              >
                {lk.linkTypeCode}
              </span>
              {lk.lag ? <LagBadge lag={lk.lag} /> : null}
            </div>
            <p
              className="mt-0.5 truncate text-[9px] font-normal leading-snug text-slate-500"
              title={`${predecessorDisplayLabel(lk, tasks)} · ${legendHint(lk.linkTypeCode)}`}
            >
              {predecessorDisplayLabel(lk, tasks)}
            </p>
          </div>
        </div>
      ))}
      {rest > 0 ? (
        <span className="inline-flex items-center gap-1 pl-0.5 text-[9px] font-semibold text-teal-700">
          <Link2 className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
          +{rest} more
        </span>
      ) : null}
    </div>
  );
}

export function CompactSuccessorLinks({
  links,
  tasks,
}: {
  links: ParsedSuccessorLink[];
  tasks: EpcTaskRef[];
}) {
  if (!links.length) {
    return (
      <span className="inline-flex items-center rounded-lg bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-400">
        —
      </span>
    );
  }
  const show = links.slice(0, 2);
  const rest = links.length - show.length;
  return (
    <div className="flex flex-col gap-1.5">
      {show.map((lk, idx) => (
        <div
          key={idx}
          className={twMerge(
            'flex min-w-0 items-start gap-2 rounded-xl border border-white/60 bg-gradient-to-r px-2 py-1.5 shadow-sm ring-1 ring-slate-200/50',
            LINK_CARD_BG[lk.linkTypeCode] ?? 'from-slate-400/8 to-white/80'
          )}
        >
          <LinkTypeDiagram
            code={lk.linkTypeCode}
            className="h-11 w-[4.75rem] shrink-0 opacity-[0.98] drop-shadow"
            aria-hidden
          />
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-center gap-1">
              <span
                className={twMerge(
                  'rounded px-1 py-0.5 font-mono text-[8px] font-black opacity-90',
                  linkPillClass(lk.linkTypeCode)
                )}
              >
                {lk.linkTypeCode}
              </span>
              {lk.lag ? <LagBadge lag={lk.lag} /> : null}
            </div>
            <p
              className="mt-0.5 truncate text-[9px] font-normal leading-snug text-slate-500"
              title={`${successorDisplayLabel(lk, tasks)} · ${legendHint(lk.linkTypeCode)}`}
            >
              {successorDisplayLabel(lk, tasks)}
            </p>
          </div>
        </div>
      ))}
      {rest > 0 ? (
        <span className="inline-flex items-center gap-1 pl-0.5 text-[9px] font-semibold text-sky-700">
          <Link2 className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
          +{rest} more
        </span>
      ) : null}
    </div>
  );
}

export function DetailLinkCardPred({
  link,
  tasks,
  contextRowIndex,
  onGoToLinkedTask,
}: {
  link: ParsedPredecessorLink;
  tasks: EpcTaskRef[];
  /** Task row whose detail sheet is open — avoids bogus self-link resolution. */
  contextRowIndex?: number | null;
  onGoToLinkedTask?: (rowIndex: number) => void;
}) {
  const linkedIdx = onGoToLinkedTask
    ? resolvePredecessorTaskIndex(link, tasks, contextRowIndex ?? undefined)
    : null;
  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br p-4 shadow-sm ring-1 ring-slate-100/80',
        LINK_CARD_BG[link.linkTypeCode] ?? 'from-slate-400/10 to-white'
      )}
    >
      <div className="flex items-start gap-4">
        <LinkTypeDiagram code={link.linkTypeCode} className="h-14 w-[5.75rem] shrink-0 drop-shadow-md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={twMerge('rounded-lg px-2 py-0.5 font-mono text-[10px] font-black', linkPillClass(link.linkTypeCode))}>
              {link.linkTypeCode}
            </span>
            {link.lag ? <LagBadge lag={link.lag} /> : null}
            <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Driver task</span>
          </div>
          <p className="mt-2 text-sm font-medium text-slate-800">
            {predecessorDisplayLabel(link, tasks, contextRowIndex ?? undefined)}
          </p>
          {link.predecessorWbs ? (
            <p className="mt-1 font-mono text-xs text-slate-500">WBS {link.predecessorWbs}</p>
          ) : null}
          <p className="mt-2 text-[11px] leading-relaxed text-slate-500">{legendHint(link.linkTypeCode)}</p>
          {onGoToLinkedTask && linkedIdx != null ? (
            <button
              type="button"
              onClick={() => onGoToLinkedTask(linkedIdx)}
              className="mt-3 text-left text-[11px] font-semibold text-teal-700 underline-offset-2 hover:underline"
            >
              Show in schedule table
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function DetailLinkCardSucc({
  link,
  tasks,
  contextRowIndex,
  onGoToLinkedTask,
}: {
  link: ParsedSuccessorLink;
  tasks: EpcTaskRef[];
  contextRowIndex?: number | null;
  onGoToLinkedTask?: (rowIndex: number) => void;
}) {
  const linkedIdx = onGoToLinkedTask
    ? resolveSuccessorTaskIndex(link, tasks, contextRowIndex ?? undefined)
    : null;
  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-2xl border border-sky-200/50 bg-gradient-to-br p-4 shadow-sm ring-1 ring-sky-100/60',
        LINK_CARD_BG[link.linkTypeCode] ?? 'from-sky-400/10 to-white'
      )}
    >
      <div className="flex items-start gap-4">
        <LinkTypeDiagram code={link.linkTypeCode} className="h-14 w-[5.75rem] shrink-0 drop-shadow-md" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={twMerge('rounded-lg px-2 py-0.5 font-mono text-[10px] font-black', linkPillClass(link.linkTypeCode))}>
              {link.linkTypeCode}
            </span>
            {link.lag ? <LagBadge lag={link.lag} /> : null}
            <span className="text-[9px] font-semibold uppercase tracking-wide text-sky-600/80">Follower task</span>
          </div>
          <p className="mt-2 text-sm font-medium text-slate-800">
            {successorDisplayLabel(link, tasks, contextRowIndex ?? undefined)}
          </p>
          {link.successorWbs ? (
            <p className="mt-1 font-mono text-xs text-slate-500">WBS {link.successorWbs}</p>
          ) : null}
          <p className="mt-2 text-[11px] leading-relaxed text-slate-500">{legendHint(link.linkTypeCode)}</p>
          {onGoToLinkedTask && linkedIdx != null ? (
            <button
              type="button"
              onClick={() => onGoToLinkedTask(linkedIdx)}
              className="mt-3 text-left text-[11px] font-semibold text-sky-800 underline-offset-2 hover:underline"
            >
              Show in schedule table
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
